import { useState } from 'react';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/format';

const PRESETS = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'This year', kind: 'ytd' },
    { label: 'All time', kind: 'all' },
];

export default function DateRangeFilter({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const from = value?.from ? new Date(value.from) : undefined;
    const to = value?.to ? new Date(value.to) : undefined;

    const applyPreset = (p) => {
        const now = new Date();
        if (p.kind === 'all') return onChange({ from: null, to: null });
        if (p.kind === 'ytd') {
            const jan = new Date(now.getFullYear(), 0, 1);
            return onChange({ from: jan.toISOString(), to: now.toISOString() });
        }
        const start = new Date(now);
        start.setDate(start.getDate() - p.days);
        onChange({ from: start.toISOString(), to: now.toISOString() });
    };

    const clear = () => onChange({ from: null, to: null });

    const label = from && to
        ? `${formatDate(from.toISOString())} → ${formatDate(to.toISOString())}`
        : 'All time';

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    data-testid="daterange-trigger"
                    className="border-[#E2E8F0] bg-transparent text-slate-900 hover:bg-[#FFFFFF] gap-2 h-9"
                >
                    <CalendarIcon className="w-4 h-4 text-[#016a6d]" />
                    <span className="text-xs tabular-nums">{label}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0 bg-[#FFFFFF] border-[#E2E8F0]" data-testid="daterange-popover">
                <div className="flex">
                    <div className="w-40 border-r border-[#E2E8F0] p-2 space-y-1">
                        {PRESETS.map(p => (
                            <button
                                key={p.label}
                                onClick={() => { applyPreset(p); setOpen(false); }}
                                data-testid={`daterange-preset-${p.label.replace(/\s+/g, '-')}`}
                                className="w-full text-left text-xs px-2 py-1.5 rounded text-slate-700 hover:bg-[#F1F5F9] hover:text-slate-900 transition-colors"
                            >
                                {p.label}
                            </button>
                        ))}
                        {(from || to) && (
                            <button onClick={clear} data-testid="daterange-clear" className="w-full text-left text-xs px-2 py-1.5 rounded text-[#FF453A] hover:bg-[#F1F5F9] flex items-center gap-1">
                                <X className="w-3 h-3" /> Clear
                            </button>
                        )}
                    </div>
                    <div className="p-2">
                        <Calendar
                            mode="range"
                            selected={{ from, to }}
                            onSelect={(range) => {
                                onChange({
                                    from: range?.from ? range.from.toISOString() : null,
                                    to: range?.to ? range.to.toISOString() : null,
                                });
                            }}
                            numberOfMonths={2}
                            className="text-slate-900"
                        />
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
